import { percentChange, periodWindows } from './calculations';
import type { DashboardSnapshot } from './types';

export interface DecisionMetric {
  value: number | null;
  prior: number | null;
  change: number | null;
  unit: 'usd' | 'count' | 'ratio';
}

export interface DecisionReceipt {
  schemaVersion: 1;
  dashboardId: string;
  generatedAt: string;
  indexedThrough: string;
  decisionWindow: { current: [string | null, string | null]; previous: [string | null, string | null] };
  trust: { state: 'DECISION-READY' | 'STALE / LIMITED'; blockers: string[]; sourceAgeSeconds: number; lastSuccessfulRefresh: string };
  completeness: {
    events: { included: number; excluded: number; total: number; coverage: number | null; reason: string };
    pools: { included: number; excluded: number; total: number; coverage: number | null; reason: string };
    fees: { included: number; excluded: number; total: number; coverage: number | null; reason: string };
    unknowns: string[];
  };
  comparison: Record<'volumeUsd' | 'transactions' | 'activeTraders' | 'returningShare', DecisionMetric>;
}

const sum = <T,>(rows: T[], read: (row: T) => number) => rows.reduce((total, row) => total + read(row), 0);
const average = <T,>(rows: T[], read: (row: T) => number | null) => {
  const values = rows.map(read).filter((value): value is number => value !== null && Number.isFinite(value));
  return values.length ? sum(values, (value) => value) / values.length : null;
};
const metric = (value: number | null, prior: number | null, unit: DecisionMetric['unit']): DecisionMetric => ({ value, prior, change: percentChange(value, prior), unit });

export function buildDecisionReceipt(snapshot: DashboardSnapshot, now = Date.now()): DecisionReceipt {
  const { current, previous } = periodWindows(snapshot.overview.series, '7');
  const sourceAgeSeconds = Math.max(0, Math.floor((now - new Date(snapshot.generatedAt).getTime()) / 1000));
  const currentCoverage = snapshot.currentCoverage;
  const poolTotal = snapshot.coverage.registryPools;
  const poolIncluded = currentCoverage?.tvlPoolsValued ?? 0;
  const feeIncluded = currentCoverage?.feePoolsValued ?? 0;
  const eventTotal = snapshot.coverage.swapEvents;
  const eventIncluded = snapshot.coverage.pricedSwapEvents;
  const eventExcluded = snapshot.coverage.unpricedSwapEvents;
  const blockers: string[] = [];
  if (sourceAgeSeconds > 86_400) blockers.push('snapshot older than 24 hours');
  if (snapshot.refresh.status !== 'complete') blockers.push('last refresh did not complete');
  if (current.length < 7 || previous.length < 7) blockers.push('two complete seven-day windows unavailable');
  if (poolIncluded / Math.max(1, poolTotal) < .95) blockers.push('TVL pool coverage below 95%');
  if (eventIncluded / Math.max(1, eventTotal) < .95) blockers.push('priced-event coverage below 95%');

  return {
    schemaVersion: 1,
    dashboardId: 'phar-gg',
    generatedAt: snapshot.generatedAt,
    indexedThrough: snapshot.coverage.window.to,
    decisionWindow: {
      current: [current[0]?.date ?? null, current.at(-1)?.date ?? null],
      previous: [previous[0]?.date ?? null, previous.at(-1)?.date ?? null],
    },
    trust: {
      state: blockers.length ? 'STALE / LIMITED' : 'DECISION-READY',
      blockers,
      sourceAgeSeconds,
      lastSuccessfulRefresh: snapshot.refresh.lastAttemptAt || snapshot.generatedAt,
    },
    completeness: {
      events: { included: eventIncluded, excluded: eventExcluded, total: eventTotal, coverage: eventTotal ? eventIncluded / eventTotal : null, reason: 'unpriced events remain in activity counts but are excluded from USD volume' },
      pools: { included: poolIncluded, excluded: Math.max(0, poolTotal - poolIncluded), total: poolTotal, coverage: poolTotal ? poolIncluded / poolTotal : null, reason: 'only pools with admitted token-price paths enter current TVL' },
      fees: { included: feeIncluded, excluded: Math.max(0, poolTotal - feeIncluded), total: poolTotal, coverage: poolTotal ? feeIncluded / poolTotal : null, reason: 'only valued current protocol-fee inventories enter the USD total' },
      unknowns: ['historical daily gross fees', 'historical gauge-level emissions', 'wallet identity behind transaction.from'],
    },
    comparison: {
      volumeUsd: metric(sum(current, (row) => row.volumeUsd), previous.length ? sum(previous, (row) => row.volumeUsd) : null, 'usd'),
      transactions: metric(sum(current, (row) => row.transactions), previous.length ? sum(previous, (row) => row.transactions) : null, 'count'),
      activeTraders: metric(average(current, (row) => row.traders), average(previous, (row) => row.traders), 'count'),
      returningShare: metric(average(current, (row) => row.returningShare), average(previous, (row) => row.returningShare), 'ratio'),
    },
  };
}

export function downloadDecisionReceipt(receipt: DecisionReceipt) {
  const blob = new Blob([`${JSON.stringify(receipt, null, 2)}\n`], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `phar-gg-decision-receipt-${receipt.decisionWindow.current[1] ?? 'unknown'}.json`;
  link.click();
  URL.revokeObjectURL(url);
}
